/**
 * Who is signed in.
 *
 * Talks to the services layer only, so it works identically on the mock,
 * Firebase and (eventually) our own backend.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { auth, presence } from '@zivapp/shared'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Fires immediately with the current state, then on every change.
    const unsubscribe = auth.onAuthChange(nextUser => {
      setUser(nextUser)
      setIsLoading(false)
    })
    return unsubscribe
  }, [])

  // Online/last-seen only runs while someone is signed in.
  useEffect(() => {
    if (!user) return undefined
    return presence.startHeartbeat()
  }, [user])

  const value = useMemo(() => ({
    user,
    isLoading,
    isSignedIn: Boolean(user),
    requestCode: auth.requestCode,
    confirmCode: auth.confirmCode,
    updateProfile: auth.updateProfile,
    signOut: auth.signOut
  }), [user, isLoading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
